import type { InferenceSession, Tensor } from "onnxruntime-web";
import {
  DETECT_CONFIDENCE,
  ORT_WASM_PATHS,
  STEPHAN_BEST_LABEL,
  STEPHAN_BEST_MODEL_PATH,
} from "./config";
import {
  mapBoxToSource,
  prepareFeedCanvas,
  FeedPrepMeta,
  PrivacyCanvas,
} from "./feedPrep";

export interface Detection {
  label: string;
  score: number;
  x: number;
  y: number;
  width: number;
  height: number;
}

type OrtModule = typeof import("onnxruntime-web");

const PERSON_CLASS_INDEX = 1;
const INPUT_SIZE = 320;

/**
 * Singleton person detector locked to stephan-best (m-p2).
 * Failures surface to callers — never fall back to another weights file.
 * onnxruntime-web is dynamic-imported on first load so OFF / unused paths
 * do not pay ORT parse cost.
 */
class StephanBestDetector {
  private session: InferenceSession | null = null;
  private ort: OrtModule | null = null;
  private loadPromise: Promise<void> | null = null;
  private inferenceQueue: Promise<unknown> = Promise.resolve();
  private feedCanvas: PrivacyCanvas | null = null;
  private preprocessCanvas: PrivacyCanvas | null = null;
  private loadFailed = false;
  /** Bumped on every release() so in-flight loads cannot reinstall a session. */
  private releaseGeneration = 0;

  isReady(): boolean {
    return this.session !== null;
  }

  didFailToLoad(): boolean {
    return this.loadFailed;
  }

  getLabel(): string {
    return STEPHAN_BEST_LABEL;
  }

  /**
   * Drop the ONNX session and scratch canvases.
   * Call when no streams need AI so we don't keep stephan-best resident.
   * Awaits any in-flight load/inference so a late create() or session.run()
   * cannot leave a leaked session or run against a freed one.
   * Always frees the session captured at start — even if a newer release()
   * bumped generation while we awaited — so rapid off→on→off cannot leak.
   * Does not touch this.session after capture (may belong to a concurrent load).
   */
  async release(): Promise<void> {
    // Invalidate in-flight loads; generation mismatch must not skip freeing
    // the session we captured below.
    this.releaseGeneration += 1;
    const session = this.session;
    const loadPromise = this.loadPromise;
    const inflightInference = this.inferenceQueue;
    this.session = null;
    this.loadPromise = null;
    this.loadFailed = false;
    this.inferenceQueue = Promise.resolve();
    this.feedCanvas = null;
    this.preprocessCanvas = null;

    if (loadPromise) {
      try {
        await loadPromise;
      } catch {
        /* ignore — load may have failed */
      }
    }

    // Drain in-flight detect before freeing the session (WASM/WebGPU unsafe otherwise).
    try {
      await inflightInference;
    } catch {
      /* ignore — detect errors must not block release */
    }

    if (session) {
      try {
        await session.release();
      } catch {
        /* ignore — best-effort free */
      }
    }
  }

  async ensureLoaded(): Promise<boolean> {
    if (this.session) {
      return true;
    }
    if (this.loadFailed) {
      return false;
    }

    if (!this.loadPromise) {
      this.loadPromise = this.load(this.releaseGeneration);
    }
    const first = this.loadPromise;
    try {
      await first;
    } catch {
      /* load errors handled below */
    }

    if (this.session) {
      return true;
    }
    if (this.loadFailed) {
      return false;
    }

    // Dead/stale promise (e.g. released while awaiting): clear and reload once.
    if (this.loadPromise === first) {
      this.loadPromise = null;
    }
    if (!this.loadPromise) {
      this.loadPromise = this.load(this.releaseGeneration);
    }
    try {
      await this.loadPromise;
      return this.session !== null;
    } catch {
      return false;
    }
  }

  private async load(generation: number): Promise<void> {
    try {
      const ort = await import("onnxruntime-web");
      if (generation !== this.releaseGeneration) {
        return;
      }
      this.ort = ort;

      ort.env.wasm.wasmPaths = ORT_WASM_PATHS;
      if (typeof document === "undefined") {
        // Inside the privacy worker. ORT's multi-threaded and proxy paths spawn
        // their own workers via DOM APIs, so they throw "document is not
        // defined" here. Single-threaded WASM is the only viable backend.
        ort.env.wasm.numThreads = 1;
        ort.env.wasm.proxy = false;
      } else {
        const cores = navigator.hardwareConcurrency || 4;
        ort.env.wasm.numThreads = Math.min(cores <= 4 ? 2 : 4, cores);
      }

      const providers: string[] = [];
      if (
        typeof document !== "undefined" &&
        typeof navigator !== "undefined" &&
        "gpu" in navigator
      ) {
        providers.push("webgpu");
      }
      providers.push("wasm");

      let created: InferenceSession;
      try {
        created = await ort.InferenceSession.create(STEPHAN_BEST_MODEL_PATH, {
          executionProviders: providers,
        });
      } catch {
        created = await ort.InferenceSession.create(STEPHAN_BEST_MODEL_PATH, {
          executionProviders: ["wasm"],
        });
      }

      if (generation !== this.releaseGeneration) {
        try {
          await created.release();
        } catch {
          /* ignore */
        }
        return;
      }

      this.session = created;
    } catch (error) {
      if (generation === this.releaseGeneration) {
        this.loadFailed = true;
        this.session = null;
      }
      throw error;
    }
  }

  async detect(
    source: CanvasImageSource,
    sourceWidth: number,
    sourceHeight: number,
    confidenceThreshold: number = DETECT_CONFIDENCE
  ): Promise<Detection[]> {
    const ready = await this.ensureLoaded();
    if (!ready || !this.session || !this.ort) {
      throw new Error(
        this.loadFailed
          ? "stephan-best failed to load"
          : "stephan-best session unavailable"
      );
    }

    const run = () =>
      this.runDetect(source, sourceWidth, sourceHeight, confidenceThreshold);
    const result = this.inferenceQueue.then(run);
    this.inferenceQueue = result.catch(() => undefined);
    return result;
  }

  private async runDetect(
    source: CanvasImageSource,
    sourceWidth: number,
    sourceHeight: number,
    confidenceThreshold: number
  ): Promise<Detection[]> {
    if (!this.session || !this.ort) {
      throw new Error("stephan-best session unavailable");
    }
    if (!this.feedCanvas) {
      this.feedCanvas = createScratchCanvas();
    }
    if (!this.preprocessCanvas) {
      this.preprocessCanvas = createScratchCanvas();
    }

    const prepMeta = prepareFeedCanvas(
      source,
      sourceWidth,
      sourceHeight,
      this.feedCanvas
    );
    const tensor = preprocess(this.ort, this.feedCanvas, this.preprocessCanvas);
    const output = await this.runSession(this.session, tensor);
    return postprocess(output, prepMeta, confidenceThreshold);
  }

  private async runSession(
    session: InferenceSession,
    tensor: Tensor
  ): Promise<Tensor> {
    const inputName = session.inputNames[0];
    const outputName = session.outputNames[0];
    try {
      const results = await session.run({ [inputName]: tensor });
      return results[outputName] as Tensor;
    } finally {
      try {
        tensor.dispose();
      } catch {
        /* ignore */
      }
    }
  }
}

function preprocess(
  ort: OrtModule,
  canvas: PrivacyCanvas,
  target: PrivacyCanvas
): Tensor {
  target.width = INPUT_SIZE;
  target.height = INPUT_SIZE;
  const ctx = target.getContext("2d", {
    willReadFrequently: true,
  }) as CanvasRenderingContext2D | OffscreenCanvasRenderingContext2D | null;
  if (!ctx) {
    throw new Error("Could not create preprocessing canvas");
  }

  ctx.drawImage(canvas, 0, 0, INPUT_SIZE, INPUT_SIZE);

  const imageData = ctx.getImageData(0, 0, INPUT_SIZE, INPUT_SIZE);
  const { data } = imageData;
  const floatData = new Float32Array(3 * INPUT_SIZE * INPUT_SIZE);

  for (let i = 0; i < INPUT_SIZE * INPUT_SIZE; i += 1) {
    const r = data[i * 4] / 255;
    const g = data[i * 4 + 1] / 255;
    const b = data[i * 4 + 2] / 255;
    floatData[i] = r;
    floatData[i + INPUT_SIZE * INPUT_SIZE] = g;
    floatData[i + 2 * INPUT_SIZE * INPUT_SIZE] = b;
  }

  return new ort.Tensor("float32", floatData, [1, 3, INPUT_SIZE, INPUT_SIZE]);
}

function createScratchCanvas(): PrivacyCanvas {
  if (typeof OffscreenCanvas !== "undefined") {
    return new OffscreenCanvas(1, 1);
  }
  return document.createElement("canvas");
}

function postprocess(
  output: Tensor,
  meta: FeedPrepMeta,
  confidenceThreshold: number
): Detection[] {
  const rows = output.data as Float32Array;
  const dims = output.dims;
  const numRows = dims[1] || 0;
  const rowWidth = dims[2] || 6;

  const detections: Detection[] = [];

  for (let i = 0; i < numRows; i += 1) {
    const offset = i * rowWidth;
    const x1 = rows[offset];
    const y1 = rows[offset + 1];
    const x2 = rows[offset + 2];
    const y2 = rows[offset + 3];
    const score = rows[offset + 4];
    const classId = Math.round(rows[offset + 5]);

    if (classId !== PERSON_CLASS_INDEX || score < confidenceThreshold) {
      continue;
    }

    const box = mapBoxToSource(x1, y1, x2, y2, meta);
    if (box.width < 1 || box.height < 1) {
      continue;
    }

    detections.push({
      label: "person",
      score,
      x: box.x,
      y: box.y,
      width: box.width,
      height: box.height,
    });
  }

  return detections.sort((a, b) => b.score - a.score);
}

export const stephanBestDetector = new StephanBestDetector();
